import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Stack,
  Avatar,
  Skeleton,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  alpha,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  Download,
  PictureAsPdf,
  GridOn,
  CalendarToday,
  LocationOn,
  Business,
  Person,
  Phone,
  Home,
  ChevronRight,
  Code,
  MoreVert,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchBatches, updateBatchStatus, updateAssessor } from "../api/tnsdc";

// Date formatter
const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

// Export handlers
const handleExportPDF = (batchData) => {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Batch Details", 14, 20);

  const rows = Object.entries(batchData || {}).map(([key, value]) => [
    key,
    typeof value === "object" ? JSON.stringify(value) : value ?? "-",
  ]);

  autoTable(doc, {
    startY: 30,
    head: [["Field", "Value"]],
    body: rows,
    styles: { fontSize: 10, cellWidth: "wrap" },
    columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 120 } },
  });

  doc.save(`batch-${batchData.BatchId}.pdf`);
};

const handleExportExcel = (batchData) => {
  import("xlsx").then((XLSX) => {
    const worksheet = XLSX.utils.json_to_sheet([batchData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BatchDetails");

    XLSX.writeFile(workbook, `batch-${batchData.BatchId}.xlsx`);
  });
};

const handleExportJSON = (batchData) => {
  const jsonString = JSON.stringify(batchData, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `batch-${batchData.BatchId}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Reusable detail item
function DetailItem({ icon, label, value }) {
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      {icon &&
        React.cloneElement(icon, {
          sx: { color: "primary.main", mt: "4px", fontSize: 20 },
        })}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2">{value ?? "Not specified"}</Typography>
      </Box>
    </Box>
  );
}

export default function BatchDetails() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  // export menu anchor
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const openExport = Boolean(exportAnchorEl);
  // actions menu anchor (for wrapping Update Status)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const openActions = Boolean(actionsAnchorEl);

  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);

  // status dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [updating, setUpdating] = useState(false);

  // assessor dialog states
  const [assessorDialogOpen, setAssessorDialogOpen] = useState(false);
  const [assessorName, setAssessorName] = useState("");
  const [assessorContactNo, setAssessorContactNo] = useState("");
  const [dateOfAssessment, setDateOfAssessment] = useState("");
  const [updatingAssessor, setUpdatingAssessor] = useState(false);

  // snackbar
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetchBatches()
      .then((res) => {
        const batchArray = res?.data?.result?.GetBatchInfo?.BatchArray || [];
        const batch = batchArray.find((b) => b.BatchId?.toString() === batchId?.toString());

        if (!batch) throw new Error("Batch not found");

        if (mounted) {
          setBatchData(batch);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Error fetching batch details:", err);
        if (mounted) setLoading(false);
      });

    return () => (mounted = false);
  }, [batchId]);

  // export menu handlers
  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  // actions menu handlers
  const handleActionsClick = (event) => setActionsAnchorEl(event.currentTarget);
  const handleActionsClose = () => setActionsAnchorEl(null);

  // open status dialog (prefill values)
  const openStatusDialog = () => {
    setNewStatus(batchData?.Status ?? "");
    setReason("");
    setStatusDialogOpen(true);
    handleActionsClose(); // close actions menu when opening dialog
  };
  const closeStatusDialog = () => {
    if (!updating) setStatusDialogOpen(false);
  };

  // open assessor dialog
  const openAssessorDialog = () => {
    setAssessorName("");
    setAssessorContactNo("");
    setDateOfAssessment("");
    setAssessorDialogOpen(true);
    handleActionsClose();
  };
  const closeAssessorDialog = () => {
    if (!updatingAssessor) setAssessorDialogOpen(false);
  };

  // submit status update
  const handleSubmitStatus = async () => {
    if (!newStatus) {
      setSnack({ open: true, message: "Please select a status", severity: "warning" });
      return;
    }

    setUpdating(true);
    try {
      const payload = {
        batchCode: batchData.BatchCode,
        status: newStatus,
        reasonOrRemarks: reason ?? "",
      };

      const res = await updateBatchStatus(payload);

      // consider success if backend returns isSuccess true or statusCode 200
      const success =
        res?.data?.isSuccess === true || res?.data?.statusCode === 200 || res.status === 200;

      if (success) {
        // optimistic UI update
        setBatchData((prev) => ({ ...prev, Status: newStatus, Remarks: reason }));
        setSnack({ open: true, message: "Batch status updated successfully", severity: "success" });
        setStatusDialogOpen(false);
      } else {
        // show sensible message if available
        const errMsg =
          res?.data?.message || res?.data?.error || JSON.stringify(res?.data) || "Unknown error from server";
        setSnack({ open: true, message: `Failed to update: ${errMsg}`, severity: "error" });
      }
    } catch (err) {
      console.error("Error calling updateBatchStatus:", err);
      setSnack({ open: true, message: "Network / server error while updating status", severity: "error" });
    } finally {
      setUpdating(false);
    }
  };

  // submit assessor update
  const handleUpdateAssessor = async () => {
    if (!assessorName || !assessorContactNo || !dateOfAssessment) {
      setSnack({ open: true, message: "Please fill all required fields", severity: "warning" });
      return;
    }

    setUpdatingAssessor(true);
    try {
      const payload = {
        batchCode: batchData.BatchCode,
        assessorName,
        assessorContactNo,
        dateOfAssessment,
      };

      const res = await updateAssessor(payload);
      
      // Check if the update was successful
      const success = res?.data?.isSuccess === true || res?.data?.statusCode === 200 || res.status === 200;
      
      if (success) {
        setSnack({ open: true, message: "Assessor details updated successfully", severity: "success" });
        setAssessorDialogOpen(false);
      } else {
        const errMsg = res?.data?.message || res?.data?.error || "Unknown error from server";
        setSnack({ open: true, message: `Failed to update: ${errMsg}`, severity: "error" });
      }
    } catch (err) {
      console.error("Error calling updateAssessor:", err);
      setSnack({ open: true, message: "Network/server error while updating assessor", severity: "error" });
    } finally {
      setUpdatingAssessor(false);
    }
  };

  const handleCloseSnack = () => setSnack((s) => ({ ...s, open: false }));

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto" }}>
        <Skeleton variant="rounded" width="100%" height={200} />
      </Box>
    );
  }

  if (!batchData) {
    return (
      <Box
        sx={{
          p: 6,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Batch not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: "auto", position: "relative" }}>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 3 }}>
        <Link
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
        >
          <Home sx={{ mr: 1}} fontSize="small" />
          Dashboard
        </Link>
        <Typography color="text.primary">Batch Details</Typography>
      </Breadcrumbs>

      {/* Export + Update Status */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4, gap: 2 }}>
        {/* left can hold page-level actions or be empty */}
        <Box />

        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Status: ${batchData?.Status ?? "N/A"}`} color="primary" variant="outlined" />

          {/* Download/export button moved to the right */}
          <Tooltip title="Export options">
            <IconButton
              onClick={handleExportClick}
              aria-label="export"
              sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
            >
              <Download />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={exportAnchorEl}
            open={openExport}
            onClose={handleExportClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{ sx: { borderRadius: 2, boxShadow: 3, p: 0.5 , minWidth: 120 } }}
          >
            <MenuItem onClick={() => { handleExportPDF(batchData); handleExportClose(); }}>
              <ListItemIcon>
                <PictureAsPdf fontSize="small" color="error" />
              </ListItemIcon>
              Export as PDF
            </MenuItem>
            <MenuItem onClick={() => { handleExportExcel(batchData); handleExportClose(); }}>
              <ListItemIcon>
                <GridOn fontSize="small" color="success" />
              </ListItemIcon>
              Export as Excel
            </MenuItem>
            <MenuItem onClick={() => { handleExportJSON(batchData); handleExportClose(); }}>
              <ListItemIcon>
                <Code fontSize="small" color="action" />
              </ListItemIcon>
              Export as JSON
            </MenuItem>
          </Menu>

          {/* Actions menu that wraps Update Batch Status */}
          <Tooltip title="Actions">
            <IconButton onClick={handleActionsClick} aria-label="actions">
              <MoreVert />
            </IconButton>
          </Tooltip>

          <Menu anchorEl={actionsAnchorEl} open={openActions} onClose={handleActionsClose} PaperProps={{ sx: { minWidth: 100, borderRadius: 2, boxShadow: 3, p: 0.5 } }}>
            <MenuItem onClick={openStatusDialog}>Update Batch Status</MenuItem>
            <MenuItem onClick={openAssessorDialog}>Update Assessor</MenuItem>
          </Menu>
        </Stack>
      </Box>

      {/* Header */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          mb: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(
            theme.palette.primary.light,
            0.05
          )} 100%)`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 400, mb: 1 }}>
                Batch Code: {batchData?.BatchCode ?? "Not specified"}
              </Typography>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <Box component="span" sx={{ color: "black" }}>
                  Job Role:
                </Box>{" "}
                {batchData?.JobRole ?? "Not specified"} <br />
                <Box component="span" sx={{ color: "black" }}>
                  Sector:
                </Box>{" "}
                {batchData?.Sector ?? "Not specified"}
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, alignItems: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Scheme Name:
                </Typography>
                <Chip label={batchData?.SchemeName ?? "Not specified"} size="small" variant="outlined" color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Batch Location:
                </Typography>
                <Chip label={batchData?.BatchLocation ?? "Not specified"} size="small" variant="outlined" icon={<LocationOn />} />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Two-column details */}
      <Grid container spacing={3}>
        {/* Training Partner */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar sx={{ mr: 2, width: 56, height: 56, bgcolor: "primary.main", fontSize: "1.5rem", fontWeight: 600 }}>
                  {batchData?.TPName ? batchData.TPName.charAt(0).toUpperCase() : "T"}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 400 }}>
                    Training Partner
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {batchData?.TPName ?? "Not specified"}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 3 }} />
              <Stack spacing={2.5}>
                <DetailItem icon={<Business />} label="TP Name" value={batchData?.TPName} />
                <DetailItem icon={<LocationOn />} label="Address" value={batchData?.TPAddress} />
                <DetailItem icon={<Person />} label=" TP SPOC Name" value={batchData?.TPSPOCName} />
                <DetailItem icon={<Phone />} label=" TP Contact" value={batchData?.TPSPOCContactNo} />
                <DetailItem icon={<LocationOn />} label="TP Location" value={batchData?.TPLocation} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Batch Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 400, mb: 1 }}>
                Batch Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Detailed information about this training batch
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <DetailItem label="Batch ID" value={batchData?.BatchId} />
                  <DetailItem label="Batch Size" value={batchData?.BatchSize} />
                  <DetailItem label="Start Date" value={formatDate(batchData?.TrainingStartDate)} icon={<CalendarToday />} />
                  <DetailItem label="Created Date" value={formatDate(batchData?.CreatedDate)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DetailItem label="End Date" value={formatDate(batchData?.TrainingEndDate)} icon={<CalendarToday />} />
                  <DetailItem label="Last Updated" value={formatDate(batchData?.LastUpdatedDate)} />
                  <DetailItem label="Status" value={batchData?.Status} />
                  <DetailItem label="Remarks" value={batchData?.Remarks} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Status Update Dialog */}
      <Dialog open={statusDialogOpen} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Batch Status</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update status for <strong>{batchData?.BatchCode}</strong>. Changes will be sent to TNSDC portal.
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select labelId="status-select-label" value={newStatus} label="Status" onChange={(e) => setNewStatus(e.target.value)}>
              {/* keep options minimal and relevant */}
              <MenuItem value="Hold">Hold</MenuItem>
              <MenuItem value="Cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Reason / Remarks" value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline rows={4} placeholder="Enter reason for status change (optional but recommended)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog} disabled={updating}> Cancel </Button>
          <Button variant="contained" onClick={handleSubmitStatus} disabled={updating} startIcon={updating ? <CircularProgress size={18} /> : null}> {updating ? "Updating..." : "Update Status"} </Button>
        </DialogActions>
      </Dialog>

      {/* Update Assessor Dialog */}
      
      <Dialog open={assessorDialogOpen} onClose={closeAssessorDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Update Assessor Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}> Update assessor details for <strong>{batchData?.BatchCode}</strong>.</Typography>
          <TextField label="Assessor Name" value={assessorName} onChange={(e) => setAssessorName(e.target.value)} fullWidth sx={{ mb: 2 }} required />
          <TextField label="Assessor Contact No" value={assessorContactNo} onChange={(e) => setAssessorContactNo(e.target.value)} fullWidth sx={{ mb: 2 }} required inputProps={{ pattern: "[0-9]{10}", title: "Please enter a valid 10-digit phone number" }} />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
  <DatePicker
    label="Date of Assessment"
    value={dateOfAssessment}
    onChange={(newValue) => setDateOfAssessment(newValue)}
    showToolbar
    renderInput={(params) => <TextField {...params} fullWidth required sx={{ mb: 2 }} />}
    slotProps={{
      actionBar: {
        actions: ['clear', 'today'], 
      },
      popper: {
        placement: 'bottom-start',
        modifiers: [
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ],
      },
    }}
    PopperProps={{ disablePortal: false }}
  />
</LocalizationProvider>


        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssessorDialog} disabled={updatingAssessor}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateAssessor} disabled={updatingAssessor} startIcon={updatingAssessor ? <CircularProgress size={18} /> : null}> {updatingAssessor ? "Updating..." : "Update Assessor"} </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={6000} onClose={handleCloseSnack} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={handleCloseSnack} severity={snack.severity} sx={{ width: "100%" }}> {snack.message} </Alert>
      </Snackbar>
    </Box>
  );
}


